var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, theme, Tooltip } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { useEventComposerGlobalContext } from '../../../composers/EventComposerGlobalContext';
import { getManagedSpace, isOrganizerSpace, isSpaceChangePending as isSpaceChangePendingSelector, } from '../../../utils/spaceOrchestration';
var actionButtonStyle = {
    width: '100%',
    alignSelf: 'flex-start',
};
/**
 * Remove/Change act on the form only, so neither needs a confirmation.
 * In EDIT mode a removal commits at save: the space is simply absent from
 * the PATCH's `space_invitees`, and the API removes it and frees the room.
 *
 * Blocked cards render a single DISABLED action with a tooltip naming the
 * reason, in precedence order:
 * - Request-only lock: a persisted request-only event's space is locked
 *   from space changes for everyone, before and after approval (see
 *   getManagedSpace).
 * - Organizer: removing the organizer space would effectively cancel the
 *   event, and changing it is a /move — only reliable against a
 *   quiescent, fully-synced event, never mid-save. Rooms change via the
 *   standalone Event Details action instead (see useOrganizerSpaceMove).
 * - Permission: the viewer lacks event_permissions.update.
 * Both locks are snapshot-derived, so CREATE mode never blocks.
 */
export var useSpaceActions = function (_a) {
    var _b;
    var spaceId = _a.spaceId, spaceInviteeId = _a.spaceInviteeId, onChangeSpace = _a.onChangeSpace, userCanRemoveSpace = _a.userCanRemoveSpace;
    var tSpaceBooking = useTranslation('spaceBooking').t;
    var token = theme.useToken().token;
    var _c = useEventComposerGlobalContext(), spacesState = _c.spacesState, initialSpaces = _c.initialSpaces, removeSpace = _c.removeSpace;
    var isSpaceChangePending = isSpaceChangePendingSelector(spacesState, spaceId);
    /* Value comes from initialSpaces, which is the initial value from an edit event */
    var isOrganizer = isOrganizerSpace(initialSpaces, spaceId);
    var isLockedRequestOnly = ((_b = getManagedSpace(initialSpaces)) === null || _b === void 0 ? void 0 : _b.id) === spaceId;
    return useMemo(function () {
        if (!userCanRemoveSpace || isOrganizer || isLockedRequestOnly) {
            // The organizer's meaningful action is Change (Remove would cancel
            // the event), so its disabled button and reason are change-flavored.
            var blockedTooltip = isOrganizer
                ? isLockedRequestOnly
                    ? tSpaceBooking('change_space.request_only_blocked')
                    : tSpaceBooking('change_space.organizer_blocked')
                : isLockedRequestOnly
                    ? tSpaceBooking('remove_space.request_only_blocked')
                    : tSpaceBooking('remove_space.permission_blocked');
            return [
                _jsx(Tooltip, { title: blockedTooltip, children: _jsx(Button, { variant: "outlined", type: "text", size: "small", disabled: true, style: actionButtonStyle, children: isOrganizer
                            ? tSpaceBooking('change_space.action')
                            : tSpaceBooking('remove_space.action') }) }, "blocked"),
            ];
        }
        return [
            _jsx(Button, { variant: "outlined", type: "text", size: "small", onClick: function () { return onChangeSpace(spaceId, spaceInviteeId); }, style: actionButtonStyle, disabled: !!isSpaceChangePending, loading: isSpaceChangePending, children: isSpaceChangePending
                    ? tSpaceBooking('change_space.changing_space')
                    : tSpaceBooking('change_space.action') }, "change"),
            _jsx(Button, { variant: "outlined", type: "text", size: "small", onClick: function () { return removeSpace(spaceId); }, style: __assign(__assign({}, actionButtonStyle), { color: token.colorError }), children: tSpaceBooking('remove_space.action') }, "remove"),
        ];
    }, [
        userCanRemoveSpace,
        isOrganizer,
        isLockedRequestOnly,
        isSpaceChangePending,
        onChangeSpace,
        removeSpace,
        spaceId,
        spaceInviteeId,
        tSpaceBooking,
        token.colorError,
    ]);
};
